import { SocketSessionRetry } from '../../components/SocketSessionRetry';
/**
 * @page DealerPage
 * @description 荷官操作台（強制橫式 PWA，含 30 秒撤銷）
 * Route: /dealer/:tournamentId/:tableId
 * UI Reference: UI-6-3
 */

import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatTime } from '../../lib/format';
import { useTranslation } from 'react-i18next';
import { useTournamentSocket } from '../../hooks/useTournamentSocket';
import { useAuthStore } from '../../store/auth.store';
import { timeoutSignal } from '../../lib/timeoutSignal';

const API = (import.meta as unknown as { env: { VITE_API_URL?: string } }).env.VITE_API_URL ?? '';

function authHeaders(): HeadersInit {
  const token = useAuthStore.getState().accessToken ?? '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function jsonOrThrow(r: Response) {
  if (!r.ok) throw new Error(`API error: ${r.status}`);
  return r.json();
}

interface SeatAssignment {
  id: string;
  entryId: string;
  seatNo: number;
}

interface PlayerEntry {
  id: string;
  nickname?: string;
  memberNo?: string;
  status: string;
  rebuys: number;
  addons: number;
  tableNo?: number;
  seatNo?: number;
  chips?: number;
}

interface TableWithSeats {
  tableId: string;
  isFinalTable: boolean;
  table: { id: string; tableNo: number; name?: string; maxSeats: number };
  seats: SeatAssignment[];
}

interface Tournament {
  id: string;
  name: string;
  status: string;
  allowRebuy: boolean;
  allowAddon: boolean;
  addonEndLevel?: number;
}

interface UndoEntry {
  id: string;
  action: 'eliminate' | 'rebuy' | 'addon';
  entryId: string;
  nickname: string;
  expiresAt: number; // timestamp
  eliminationId?: string;
  rebuyId?: string;
  addonId?: string;
}

// ─── Landscape Lock ─────────────────────────────────────────────────────────

function LandscapeLockScreen() {
  const { t } = useTranslation('tournament');
  return (
    <div className="fixed inset-0 bg-slate-900 flex flex-col items-center justify-center text-white z-[9999]">
      <span className="material-symbols-outlined text-8xl text-primary mb-4 animate-bounce">
        screen_rotation
      </span>
      <h1 className="text-3xl font-black mb-2">{t('dealer.rotateDevice')}</h1>
      <p className="text-slate-400 text-lg">{t('dealer.needsLandscape')}</p>
    </div>
  );
}

// ─── Elimination Confirmation ────────────────────────────────────────────────

interface EliminateModalProps {
  entry: PlayerEntry;
  entries: PlayerEntry[];
  onConfirm: (killerEntryId: string | null, isBounty: boolean) => void;
  onCancel: () => void;
}

function EliminateModal({ entry, entries, onConfirm, onCancel }: EliminateModalProps) {
  const { t } = useTranslation('tournament');
  const [killerEntryId, setKillerEntryId] = useState('');
  const activePlayers = entries.filter((e) => e.status === 'ACTIVE' && e.id !== entry.id);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-slate-900 border border-red-900/40 rounded-xl p-8 w-full max-w-md shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <span className="material-symbols-outlined text-red-500 text-4xl">person_remove</span>
          <div>
            <h3 className="text-xl font-black">{t('dealer.confirmEliminateTitle')}</h3>
            <p className="text-slate-400 text-sm">
              {t('dealer.playerLabel', { name: entry.nickname ?? entry.memberNo })}
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">{t('dealer.killerLabel')}</label>
          <select
            className="w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm"
            value={killerEntryId}
            onChange={(e) => setKillerEntryId(e.target.value)}
          >
            <option value="">{t('dealer.noKiller')}</option>
            {activePlayers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nickname ?? p.memberNo ?? p.id.slice(0, 8)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-3 mt-8">
          <button
            onClick={onCancel}
            className="flex-1 min-h-[44px] rounded-xl border border-slate-700 text-slate-300 font-bold text-lg"
          >
            {t('cancel')}
          </button>
          <button
            onClick={() => onConfirm(killerEntryId || null, !!killerEntryId)}
            className="flex-1 min-h-[44px] rounded-xl bg-red-600 hover:bg-red-500 text-white font-black text-lg transition-colors"
          >
            {t('dealer.confirmEliminateTitle')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Undo Toast ──────────────────────────────────────────────────────────────

interface UndoToastProps {
  undo: UndoEntry;
  onUndo: () => void;
  onDismiss: () => void;
}

function UndoToast({ undo, onUndo, onDismiss }: UndoToastProps) {
  const { t } = useTranslation('tournament');
  const [remaining, setRemaining] = useState(Math.ceil((undo.expiresAt - Date.now()) / 1000));

  useEffect(() => {
    const interval = setInterval(() => {
      const left = Math.ceil((undo.expiresAt - Date.now()) / 1000);
      setRemaining(left);
      if (left <= 0) {
        onDismiss();
        clearInterval(interval);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [undo.expiresAt, onDismiss]);

  const actionLabel =
    undo.action === 'eliminate'
      ? t('dealer.actionEliminate')
      : undo.action === 'rebuy'
        ? t('dealer.actionRebuy')
        : t('dealer.actionAddon');

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 bg-slate-800 border border-primary/30 rounded-xl px-6 py-4 shadow-2xl">
      <div className="text-sm">
        <span className="font-bold text-white">{undo.nickname}</span>
        <span className="text-slate-400 ml-1">
          {t('dealer.actionRecorded', { action: actionLabel })}
        </span>
      </div>
      <button
        onClick={onUndo}
        className="min-h-[44px] px-5 rounded-lg bg-primary text-black font-black text-sm"
      >
        {t('dealer.undo')} ({remaining}s)
      </button>
      <button onClick={onDismiss} className="min-h-[44px] px-3 text-slate-500 hover:text-white">
        <span className="material-symbols-outlined text-xl">close</span>
      </button>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function DealerPage() {
  const { t } = useTranslation('tournament');
  const { tournamentId, tableId } = useParams<{ tournamentId: string; tableId: string }>();
  const qc = useQueryClient();
  const token = useAuthStore((s) => s.accessToken) ?? '';
  const undoTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (undoTimeout.current) clearTimeout(undoTimeout.current);
    },
    [],
  );

  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);
  const [eliminateTarget, setEliminateTarget] = useState<PlayerEntry | null>(null);
  const [pendingUndo, setPendingUndo] = useState<UndoEntry | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list');

  // Detect orientation
  useEffect(() => {
    const handler = () => setIsPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
    };
  }, []);

  const { sessionPaused, retrySocket, timerUpdate, connected } = useTournamentSocket(
    tournamentId ?? '',
    token,
    !!tournamentId,
  );

  const remainingSeconds = timerUpdate?.timerState?.remainingSeconds ?? 0;
  const isRunning = timerUpdate?.timerState?.isRunning ?? false;
  const [countdown, setCountdown] = useState(remainingSeconds);

  useEffect(() => {
    setCountdown(remainingSeconds);
  }, [remainingSeconds]);
  useEffect(() => {
    if (!isRunning) return;
    const iv = setInterval(() => setCountdown((p) => Math.max(0, p - 1)), 1000);
    return () => clearInterval(iv);
  }, [isRunning, remainingSeconds]);

  const { data: layout } = useQuery<TableWithSeats[]>({
    queryKey: ['tournament-layout', tournamentId],
    queryFn: () =>
      fetch(`${API}/api/venue/tournaments/${tournamentId}/layout`, {
        headers: authHeaders(),
        signal: timeoutSignal(10000),
      }).then(jsonOrThrow),
    enabled: !!tournamentId,
    refetchInterval: 30_000,
  });

  const currentTable = layout?.find((t) => t.tableId === tableId);

  const { data: tournament } = useQuery<Tournament>({
    queryKey: ['tournament', tournamentId],
    queryFn: () =>
      fetch(`${API}/api/tournament/${tournamentId}`, {
        headers: authHeaders(),
        signal: timeoutSignal(10000),
      }).then(jsonOrThrow),
    enabled: !!tournamentId,
  });

  const { data: allEntries = [] } = useQuery<PlayerEntry[]>({
    queryKey: ['tournament-entries', tournamentId],
    queryFn: () =>
      fetch(`${API}/api/tournament/${tournamentId}/players`, {
        headers: authHeaders(),
        signal: timeoutSignal(10000),
      })
        .then(jsonOrThrow)
        .then((d) => d.items ?? d),
    enabled: !!tournamentId,
    refetchInterval: 30_000,
  });

  // Entries at this table
  const tableEntryIds = new Set(currentTable?.seats.map((s) => s.entryId) ?? []);
  const tableEntries = allEntries.filter((e) => tableEntryIds.has(e.id));
  const seatedEntries =
    currentTable?.seats.map((seat) => {
      const entry = allEntries.find((e) => e.id === seat.entryId);
      return { seat, entry };
    }) ?? [];

  interface ActionBody {
    entryId: string;
    killerEntryId?: string;
  }

  const eliminateMutation = useMutation({
    mutationFn: (body: ActionBody) =>
      fetch(`${API}/api/tournament/${tournamentId}/eliminate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
        signal: timeoutSignal(10000),
      }).then(jsonOrThrow),
    onSuccess: (data, variables) => {
      qc.invalidateQueries({ queryKey: ['tournament-entries', tournamentId] });
      const entry = allEntries.find((e) => e.id === variables.entryId);
      const undoEntry: UndoEntry = {
        id: crypto.randomUUID(),
        action: 'eliminate',
        entryId: variables.entryId,
        nickname: entry?.nickname ?? variables.entryId.slice(0, 8),
        expiresAt: Date.now() + 30_000,
        eliminationId: (data as { id?: string })?.id,
      };
      setPendingUndo(undoEntry);
      if (undoTimeout.current) clearTimeout(undoTimeout.current);
      undoTimeout.current = setTimeout(() => setPendingUndo(null), 30_000);
    },
    onError: (err) => {
      console.warn('[DealerPage] eliminate failed:', (err as Error).message);
    },
  });

  const rebuyMutation = useMutation({
    mutationFn: (body: ActionBody) =>
      fetch(`${API}/api/tournament/${tournamentId}/rebuy`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
        signal: timeoutSignal(10000),
      }).then(jsonOrThrow),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['tournament-entries', tournamentId] });
      const entry = allEntries.find((e) => e.id === variables.entryId);
      setPendingUndo({
        id: crypto.randomUUID(),
        action: 'rebuy',
        entryId: variables.entryId,
        nickname: entry?.nickname ?? variables.entryId.slice(0, 8),
        expiresAt: Date.now() + 30_000,
      });
    },
    onError: (err) => {
      console.warn('[DealerPage] rebuy failed:', (err as Error).message);
    },
  });

  const addonMutation = useMutation({
    mutationFn: (body: ActionBody) =>
      fetch(`${API}/api/tournament/${tournamentId}/addon`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
        signal: timeoutSignal(10000),
      }).then(jsonOrThrow),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['tournament-entries', tournamentId] });
      const entry = allEntries.find((e) => e.id === variables.entryId);
      setPendingUndo({
        id: crypto.randomUUID(),
        action: 'addon',
        entryId: variables.entryId,
        nickname: entry?.nickname ?? variables.entryId.slice(0, 8),
        expiresAt: Date.now() + 30_000,
      });
    },
    onError: (err) => {
      console.warn('[DealerPage] addon failed:', (err as Error).message);
    },
  });

  // TODO: implement undo via a dedicated endpoint when available
  const handleUndo = () => {
    setPendingUndo(null);
  };

  if (isPortrait) return <LandscapeLockScreen />;

  return (
    <div className="bg-[#101922] text-slate-100 h-screen w-screen flex flex-col overflow-hidden">
      <SocketSessionRetry paused={sessionPaused} retry={retrySocket} />
      {/* Header */}
      <header className="flex items-center justify-between border-b border-slate-800 px-4 py-3 shrink-0">
        <div className="flex items-center gap-4">
          <span className="material-symbols-outlined text-primary text-2xl">casino</span>
          <h2 className="text-lg font-bold leading-tight">
            {t('venue:tableLabel', { no: currentTable?.table.tableNo ?? '...' })} —{' '}
            {tournament?.name ?? ''}
          </h2>
          {!connected && (
            <span className="text-xs text-amber-400 bg-amber-900/30 px-2 py-0.5 rounded">
              {t('dealer.wsShort')}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('list')}
            className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              viewMode === 'list'
                ? 'bg-primary text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <span className="material-symbols-outlined text-xl">view_list</span>
          </button>
          <div className="flex items-center gap-2 bg-primary/20 text-primary px-4 rounded-lg border border-primary/30 ml-2">
            <span className="material-symbols-outlined">timer</span>
            <span
              className="font-bold font-mono text-lg"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatTime(countdown)}
            </span>
          </div>
        </div>
      </header>

      {/* Player list */}
      <main className="flex-1 overflow-y-auto px-4 py-3">
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-800/80 border-b border-slate-800">
                <th className="px-6 py-4 text-slate-400 w-16 text-sm font-semibold">
                  {t('dealer.seatHeader')}
                </th>
                <th className="px-6 py-4 text-slate-200 text-sm font-semibold min-w-[150px]">
                  {t('dealer.playerHeader')}
                </th>
                <th className="px-6 py-4 text-slate-200 text-sm font-semibold min-w-[80px]">
                  {t('dealer.rebuyHeader')}
                </th>
                <th className="px-6 py-4 text-slate-200 text-sm font-semibold min-w-[100px]">
                  {t('dealer.statusHeader')}
                </th>
                <th className="px-6 py-4 text-slate-200 text-sm font-semibold text-right">
                  {t('dealer.actionsHeader')}
                </th>
              </tr>
            </thead>
            <tbody>
              {seatedEntries.map(({ seat, entry }) => {
                if (!entry) {
                  return (
                    <tr key={seat.id} className="border-b border-slate-800/50 opacity-40">
                      <td className="h-16 px-6 py-3 text-slate-500 font-mono text-base font-medium">
                        {seat.seatNo}
                      </td>
                      <td colSpan={4} className="h-16 px-6 py-3 text-slate-500 text-sm italic">
                        {t('dealer.emptySlot')}
                      </td>
                    </tr>
                  );
                }

                const isActive = entry.status === 'ACTIVE';
                const isEliminated = entry.status === 'ELIMINATED';

                return (
                  <tr
                    key={seat.id}
                    className={`border-b border-slate-800/50 transition-colors ${
                      isEliminated ? 'bg-slate-800/20 opacity-60' : 'hover:bg-slate-800/30'
                    }`}
                  >
                    <td className="h-16 px-6 py-3 text-slate-500 font-mono text-base font-medium">
                      {seat.seatNo}
                    </td>
                    <td className="h-16 px-6 py-3 font-medium text-base">
                      {entry.nickname ?? entry.memberNo ?? entry.id.slice(0, 8)}
                    </td>
                    <td className="h-16 px-6 py-3 text-slate-400 text-sm">
                      {entry.rebuys > 0 && <span className="text-primary">{entry.rebuys}x</span>}
                    </td>
                    <td className="h-16 px-6 py-3">
                      <span
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium ${
                          isActive
                            ? 'bg-green-900/30 text-green-400'
                            : 'bg-slate-800 text-slate-400'
                        }`}
                      >
                        <span
                          className={`size-2 rounded-full ${isActive ? 'bg-green-500' : 'bg-slate-500'}`}
                        />
                        {isActive ? t('dealer.aliveStatus') : t('dealer.eliminatedStatus')}
                      </span>
                    </td>
                    <td className="h-16 px-6 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        {isActive && (
                          <button
                            onClick={() => setEliminateTarget(entry)}
                            disabled={eliminateMutation.isPending}
                            className="min-h-[44px] px-4 py-1.5 rounded bg-red-900/30 text-red-400 hover:bg-red-900/50 text-sm font-medium transition-colors disabled:opacity-50"
                          >
                            {t('eliminate')}
                          </button>
                        )}
                        {(isEliminated || isActive) && tournament?.allowRebuy && (
                          <button
                            onClick={() =>
                              rebuyMutation.mutate({ entryId: entry.id } as ActionBody)
                            }
                            disabled={rebuyMutation.isPending}
                            className="min-h-[44px] px-4 py-1.5 rounded bg-primary/10 text-primary hover:bg-primary/20 text-sm font-medium transition-colors disabled:opacity-50"
                          >
                            {t('rebuy')}
                          </button>
                        )}
                        {isActive && tournament?.allowAddon && (
                          <button
                            onClick={() =>
                              addonMutation.mutate({ entryId: entry.id } as ActionBody)
                            }
                            disabled={addonMutation.isPending}
                            className="min-h-[44px] px-4 py-1.5 rounded bg-slate-800 text-slate-300 hover:bg-slate-700 text-sm font-medium transition-colors disabled:opacity-50"
                          >
                            {t('addon')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {seatedEntries.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-slate-500">
                    {t('dealer.noPlayersSeated')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Elimination Confirmation Modal */}
      {eliminateTarget && (
        <EliminateModal
          entry={eliminateTarget}
          entries={tableEntries}
          onConfirm={(killerEntryId) => {
            eliminateMutation.mutate({
              entryId: eliminateTarget.id,
              killerEntryId: killerEntryId ?? undefined,
            });
            setEliminateTarget(null);
          }}
          onCancel={() => setEliminateTarget(null)}
        />
      )}

      {/* Undo Toast */}
      {pendingUndo && (
        <UndoToast undo={pendingUndo} onUndo={handleUndo} onDismiss={() => setPendingUndo(null)} />
      )}
    </div>
  );
}
